import styled, { css } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import type { TTableCellContent } from '../atoms';
import { TableCellContent, TransparentIconButton } from '../atoms';
import type { Merge } from 'simplytyped';
import type {
  CSSProperties,
  PropsWithChildren,
  ReactElement,
  ReactNode
} from 'react';

type TTableCell = {
  breakAll?: boolean;
  reducePadding?: boolean;
};

export type TableHeaderCell = {
  id?: string;
  name: string;
  cellStyling?: CSSProperties;
  content?: ReactNode | ReactElement;
  onDelete?: (col: TableHeaderCell) => void;
  onEdit?: () => void;
};

export type TDisplayTableHead = {
  cols: Array<TableHeaderCell>;
};

export type TTableHead = {
  width?: string;
  isEditing?: boolean;
  verticalAlign?: string;
};

export type TComplexTableRow = {
  cells?: ReadonlyArray<
    Merge<
      TTableCellContent,
      {
        amount?: number;
        breakAll?: boolean;
      }
    >
  >;
};

const ComplexTable = styled.table(
  ({ theme }) => css`
    border-collapse: collapse;
    width: 100%;
    @media (max-width: ${theme.breakpoints.max.sm}) {
      font-size: ${theme.fonts.sizes.h6};
    }
  `
);

export const TableHeadRow = styled.thead(
  ({ theme }) => css`
    padding: 20px;
    text-align: left;
    border-bottom: ${theme.borders.normal};
  `
);

const TableRow = styled.tr(
  ({ theme }) => css`
    text-align: left;
    border-bottom: ${theme.borders.light};
  `
);

const FlexHeadWrapper = styled.span<TTableHead>(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;

    > span:nth-child(2) {
      margin-right: -15px;
    }
  `
);

const ComplexTableHeadCell = styled.th<TTableHead>(
  ({ theme, verticalAlign = 'top', isEditing = false }) => css`
    padding: 5px ${isEditing ? theme.paddings.xs : theme.paddings.sm}
      ${theme.paddings.xs};
    border-right: ${theme.borders.normal};
    vertical-align: ${verticalAlign};
    line-height: 22px;

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      border-right: none;
    }
  `
);

export const TableCell = styled.td<TTableCell>(
  ({ theme, breakAll, reducePadding = false }) => css`
    text-align: left;
    ${reducePadding
      ? css`
          padding: ${theme.paddings.xs} ${theme.paddings.xs};
        `
      : css`
          padding: ${theme.paddings.xs} ${theme.paddings.sm};
        `}
    word-break: ${breakAll ? 'break-all' : 'break-word'};
    vertical-align: top;
    border-right: ${theme.borders.normal};

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      border-right: none;
    }
  `
);

const ComplexTableHead = ({ cols }: TDisplayTableHead) => {
  return (
    <TableHeadRow>
      <tr>
        {cols.map((item) => (
          <ComplexTableHeadCell
            key={uuidv4()}
            style={'cellStyling' in item ? item.cellStyling : {}}
          >
            <FlexHeadWrapper>
              {!('content' in item) ? <span>{item.name}</span> : item.content}
              {('onDelete' in item || 'onEdit' in item) && (
                <span>
                  {'onEdit' in item && (
                    <TransparentIconButton onClick={item.onEdit}>
                      <span className="material-symbols-rounded">edit</span>
                    </TransparentIconButton>
                  )}
                  {'onDelete' in item && item.onDelete && (
                    <TransparentIconButton onClick={() => item.onDelete!(item)}>
                      <span className="material-symbols-rounded">delete</span>
                    </TransparentIconButton>
                  )}
                </span>
              )}
            </FlexHeadWrapper>
          </ComplexTableHeadCell>
        ))}
      </tr>
    </TableHeadRow>
  );
};

const ComplexTableRow = ({
  cells = [],
  children
}: PropsWithChildren<TComplexTableRow>) => {
  return (
    <TableRow>
      {children}
      {cells.map(({ breakAll, amount, ..._rest }, index) => {
        const rest = _rest as TTableCellContent;

        return (
          <TableCell key={index} breakAll={breakAll}>
            <TableCellContent {...rest} />
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export {
  ComplexTable,
  ComplexTableHead,
  ComplexTableRow,
  ComplexTableHeadCell
};
